/**
 * Generates @keyframes for compositor-friendly collapse/expand animations.
 *
 * Instead of animating `height` (which forces layout on every frame),
 * we animate `transform: scaleY()` on the card and an inverse scaleY
 * on its children so content doesn't appear squished.
 *
 * The cubic-bezier easing is baked into the keyframe percentages so the
 * animation can run with `animation-timing-function: linear` — keeping
 * the entire animation on the compositor thread.
 */

// Shared <style> element for all generated keyframes
let styleEl: HTMLStyleElement | null = null;

// Cache: "fromHeight-toHeight" → animation base name
const cache = new Map<string, string>();
let counter = 0;

// Collapse: slight upward squeeze (scale up ~2%) before folding down.
// Gives the feeling of gathering energy before snapping shut.
function squeezeCollapseEase(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;

  // Phase 1 (0–15%): scale UP slightly (squeeze)
  // Phase 2 (15–100%): fold down with standard ease
  const squeezeEnd = 0.15;
  const squeezePeak = -0.03; // negative = scale goes above starting value

  if (t < squeezeEnd) {
    // Sine curve for smooth up-and-back
    const p = t / squeezeEnd;
    return squeezePeak * Math.sin(p * Math.PI);
  }

  // Remap remaining range [squeezeEnd, 1] → [0, 1] with ease-out
  const p = (t - squeezeEnd) / (1 - squeezeEnd);
  // cubic ease-out
  const eased = 1 - Math.pow(1 - p, 3);
  return eased;
}

/**
 * Spring easing — overshoots the target then settles back.
 * Used on expand (unfold) to give a "blind dropping" feel.
 *
 * Based on a damped spring: e^(-bt) * cos(wt)
 * b = damping, w = frequency
 */
function springEase(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const b = 5;   // damping — lower = more overshoot
  const w = 2.0; // frequency — one clean overshoot + settle
  return 1 - Math.exp(-b * t) * Math.cos(w * Math.PI * t);
}

function ensureStyleElement(): HTMLStyleElement {
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'collapse-animation-keyframes';
    document.head.appendChild(styleEl);
  }
  return styleEl;
}

/**
 * Returns the animation base name for a given height pair.
 * Generates and injects @keyframes on first call for each unique pair.
 *
 * Usage:
 *   const name = getOrCreateKeyframes(325, 102);
 *   card.style.animation = `${name}-parent 200ms linear both`;
 *   child.style.animation = `${name}-child 200ms linear both`;
 */
export function getOrCreateKeyframes(fromHeight: number, toHeight: number): string {
  const key = `${Math.round(fromHeight)}-${Math.round(toHeight)}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const name = `collapse-${counter++}`;
  const steps = 60;

  // The card's CSS already has the TARGET height applied (React rendered the new class).
  // We scale from (fromHeight/toHeight) → 1 so it visually starts at old height.
  const scaleFrom = fromHeight / toHeight;
  const scaleTo = 1;

  // Expanding (unfold): use spring easing for overshoot effect.
  // Collapsing (fold): use standard ease for a clean fold.
  const isExpanding = toHeight > fromHeight;
  const easeFn = isExpanding ? springEase : squeezeCollapseEase;

  let parentFrames = `@keyframes ${name}-parent {\n`;
  let childFrames = `@keyframes ${name}-child {\n`;

  for (let i = 0; i <= steps; i++) {
    const pct = ((i / steps) * 100).toFixed(2);
    const t = easeFn(i / steps);
    const s = scaleFrom + (scaleTo - scaleFrom) * t;
    const inv = 1 / s;

    parentFrames += `  ${pct}% { transform: scaleY(${s.toFixed(4)}); }\n`;
    childFrames += `  ${pct}% { transform: scaleY(${inv.toFixed(4)}); }\n`;
  }

  parentFrames += '}';
  childFrames += '}';

  const el = ensureStyleElement();
  el.textContent += `\n${parentFrames}\n${childFrames}\n`;

  cache.set(key, name);
  return name;
}
